import { centsToDollars } from "helpers/price";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import {
  useDeleteDirectPayLinkMutation,
  useDeactivateDirectPayLinkMutation,
} from "store/queries/directPayLinks";
import styled from "styled-components";
import { IDirectPayLink } from "types/DirectPayLink.d";

import {
  copyToClipboard,
  formatShortLink,
  getDirectPayLinkStatus,
  getOrdersCreatedPercentage,
  getOrdersProgressColor,
  getStatusColor,
} from "./utils";

const StyledDataTable = styled(DataTable)`
  .main-link-row {
    background-color: var(--primary-50) !important;
    box-shadow: inset 0 0 0 1px var(--primary-100);
  }
`;

interface IDirectPayLinkTableProps {
  links: IDirectPayLink[];
  onEdit: (link: IDirectPayLink) => void;
}

export function DirectPayLinkTable({
  links,
  onEdit,
}: IDirectPayLinkTableProps) {
  const toast = useRef<Toast>(null);
  const [deleteLink] = useDeleteDirectPayLinkMutation();
  const [deactivateLink] = useDeactivateDirectPayLinkMutation();

  const handleCopyLink = async (shortId: string) => {
    const link = formatShortLink(shortId);
    const success = await copyToClipboard(link);
    if (success) {
      toast.current?.show({
        severity: "success",
        summary: "Link Copied",
        detail: "Direct pay link has been copied to clipboard",
        life: 3000,
      });
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Copy Failed",
        detail: "Could not copy link to clipboard",
        life: 3000,
      });
    }
  };

  const handleDelete = (link: IDirectPayLink) => {
    const hasOrders = link.ordersCreated > 0;
    const message = hasOrders
      ? `This link has been used to create ${link.ordersCreated} estimate(s). Deleting will deactivate the link but preserve estimate history. Continue?`
      : "Are you sure you want to delete this link? This action cannot be undone.";

    confirmDialog({
      message,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteLink(link.id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: hasOrders ? "Link Deactivated" : "Link Deleted",
            detail: hasOrders
              ? "Direct pay link has been deactivated and estimate history preserved"
              : "Direct pay link has been deleted successfully",
            life: 3000,
          });
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Delete Failed",
            detail: "Could not delete the direct pay link",
            life: 3000,
          });
        }
      },
    });
  };

  const handleToggleActive = async (link: IDirectPayLink) => {
    const newActiveStatus = !link.isActive;
    const action = newActiveStatus ? "activate" : "deactivate";

    confirmDialog({
      message: `Are you sure you want to ${action} this direct pay link?`,
      header: "Confirmation",
      icon: "pi pi-question-circle",
      accept: async () => {
        try {
          await deactivateLink({
            id: link.id,
            isActive: newActiveStatus,
          }).unwrap();
          toast.current?.show({
            severity: "success",
            summary: `Link ${newActiveStatus ? "Activated" : "Deactivated"}`,
            detail: `Direct pay link has been ${action}d successfully`,
            life: 3000,
          });
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Action Failed",
            detail: `Could not ${action} the direct pay link`,
            life: 3000,
          });
        }
      },
    });
  };

  const shortLinkTemplate = (rowData: IDirectPayLink) => {
    if (rowData.isMain) {
      return <span className="font-semibold">Main link</span>;
    }

    const link = formatShortLink(rowData.shortId);
    return (
      <div className="flex align-items-center gap-2">
        <code
          style={{
            fontSize: "0.875rem",
            padding: "0.25rem 0.5rem",
            backgroundColor: "#f3f4f6",
            borderRadius: "4px",
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {link}
        </code>
        <Button
          icon="pi pi-copy"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleCopyLink(rowData.shortId)}
          tooltip="Copy link"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const cptCodeTemplate = (rowData: IDirectPayLink) => {
    return (
      <div>
        <div className="font-semibold">{rowData.cptCode}</div>
        {rowData.cptDescription && (
          <div className="text-sm text-gray-600">{rowData.cptDescription}</div>
        )}
      </div>
    );
  };

  const amountTemplate = (rowData: IDirectPayLink) => {
    return (
      <div className="flex align-items-center gap-2">
        {rowData.amountInCents != null ? (
          <>
            <span className="font-semibold">
              ${centsToDollars(rowData.amountInCents).toFixed(2)}
            </span>
            {!rowData.amountEditable && (
              <Tag value="Fixed" severity="info" className="text-xs" />
            )}
          </>
        ) : (
          <span className="text-gray-500 italic">Not set</span>
        )}
      </div>
    );
  };

  const ordersTemplate = (rowData: IDirectPayLink) => {
    if (rowData.isMain) {
      return <span className="text-gray-400">—</span>;
    }

    const percentage = getOrdersCreatedPercentage(
      rowData.ordersCreated,
      rowData.maxOrders
    );
    const color = getOrdersProgressColor(percentage);

    return (
      <div>
        <div className="flex justify-content-between mb-1">
          <span className="text-sm">
            {rowData.ordersCreated} / {rowData.maxOrders}
          </span>
          <span className="text-sm">{percentage.toFixed(0)}%</span>
        </div>
        <ProgressBar
          value={percentage}
          showValue={false}
          style={{ height: "6px" }}
          color={color}
        />
      </div>
    );
  };

  const statusTemplate = (rowData: IDirectPayLink) => {
    if (rowData.isMain) {
      const status = rowData.isActive ? "Active" : "Inactive";
      const severity = rowData.isActive ? "success" : "danger";
      return <Tag value={status} severity={severity} />;
    }

    const status = getDirectPayLinkStatus(rowData);
    const severity = getStatusColor(status);
    return <Tag value={status} severity={severity as any} />;
  };

  const daysLeftTemplate = (rowData: IDirectPayLink) => {
    if (rowData.isMain) {
      return <span className="text-gray-400">—</span>;
    }

    if (rowData.expiresAt == null) {
      return <span className="text-green-700">No expiration</span>;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expirationDate = new Date(rowData.expiresAt);
    expirationDate.setHours(0, 0, 0, 0);

    const diffTime = expirationDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return <span className="text-red-500">Expired</span>;
    }
    if (diffDays === 0) {
      return <span className="text-orange-500">Today</span>;
    }
    if (diffDays === 1) {
      return <span className="text-orange-500">1 day</span>;
    }
    if (diffDays <= 7) {
      return <span className="text-orange-500">{diffDays} days</span>;
    }
    if (diffDays <= 30) {
      return <span className="text-yellow-600">{diffDays} days</span>;
    }
    return <span>{diffDays} days</span>;
  };

  const actionsTemplate = (rowData: IDirectPayLink) => {
    const status = getDirectPayLinkStatus(rowData);
    const isExpired = status === "Expired" && !rowData.isMain;

    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => onEdit(rowData)}
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon={rowData.isActive ? "pi pi-eye-slash" : "pi pi-eye"}
          className="p-button-rounded p-button-text p-button-sm p-button-warning"
          onClick={() => handleToggleActive(rowData)}
          tooltip={rowData.isActive ? "Deactivate" : "Activate"}
          tooltipOptions={{ position: "top" }}
          disabled={isExpired}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-sm p-button-danger"
          onClick={() => handleDelete(rowData)}
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <StyledDataTable
        value={links}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage="No direct pay links created yet."
        responsiveLayout="scroll"
        stripedRows
        rowClassName={(data: IDirectPayLink) =>
          data.isMain ? "main-link-row" : ""
        }
      >
        <Column
          field="shortLink"
          header="Short Link"
          body={shortLinkTemplate}
          style={{ minWidth: "300px" }}
        />
        <Column
          field="cptCode"
          header="CPT Code"
          body={cptCodeTemplate}
          style={{ minWidth: "150px" }}
        />
        <Column
          field="amountInCents"
          header="Amount"
          body={amountTemplate}
          style={{ minWidth: "120px" }}
        />
        <Column
          field="ordersCreated"
          header="Estimates Created"
          body={ordersTemplate}
          style={{ minWidth: "200px" }}
        />
        <Column
          field="status"
          header="Status"
          body={statusTemplate}
          style={{ minWidth: "120px" }}
        />
        <Column
          field="expiresAt"
          header="Days Left"
          body={daysLeftTemplate}
          style={{ minWidth: "120px" }}
        />
        <Column
          header="Actions"
          body={actionsTemplate}
          style={{ minWidth: "180px" }}
        />
      </StyledDataTable>
    </>
  );
}
